// Admin and Authentication API module
import { BaseApiClient } from './BaseApiClient';
import type {
  ApiResponse,
  ApiPaginatedResponse,
  Alert,
  AuditLog,
} from '@/types/api';
import type {
  AdminUser,
  AdminRole,
  PaginationParams,
  CustomRoleConfig,
  RoleConfigUpdateRequest,
  RoleConfigResponse,
} from '@/types/admin';

interface AdminInfoPayload {
  role?: string;
  permissions?: string[];
  last_login_at?: string;
  email?: string;
  user_id?: string;
  domain?: string;
  [key: string]: unknown;
}

interface VerifyAdminAccessPayload {
  is_admin: boolean;
  admin_info?: AdminInfoPayload;
}

interface VerifyAdminAccessResponse {
  is_admin: boolean;
  admin_info?: AdminInfoPayload;
  data?: VerifyAdminAccessPayload;
  status?: string;
  message?: string;
  timestamp?: string;
}

interface RequestErrorWithStatus {
  response?: {
    status?: number;
  };
}

export class AdminAuthApi extends BaseApiClient {
  constructor() {
    super('AdminAuthAPI');
  }

  // Alerts API
  async getAlerts(
    filters?: { status?: string; type?: string; severity?: string },
    pagination?: PaginationParams
  ): Promise<ApiPaginatedResponse<Alert>> {
    const params = { ...filters, ...pagination };
    const response = await this.api.get<ApiPaginatedResponse<Alert>>('/alerts', { params });
    return response.data;
  }

  async acknowledgeAlert(alertId: string): Promise<ApiResponse<void>> {
    const response = await this.api.patch<ApiResponse<void>>(`/alerts/${alertId}/acknowledge`);
    return response.data;
  }

  async resolveAlert(alertId: string, resolution?: string): Promise<ApiResponse<void>> {
    const response = await this.api.patch<ApiResponse<void>>(`/alerts/${alertId}/resolve`, { resolution });
    return response.data;
  }

  async dismissAlert(alertId: string): Promise<ApiResponse<void>> {
    const response = await this.api.patch<ApiResponse<void>>(`/alerts/${alertId}/dismiss`);
    return response.data;
  }

  // Admin User Management API
  async getAdminUsers(
    filters?: { status?: string; role?: string },
    pagination?: PaginationParams
  ): Promise<ApiPaginatedResponse<AdminUser>> {
    // Use the actual backend endpoint: /api/v1/admin/admins
    const params = { ...filters, ...pagination };
    const response = await this.api.get<ApiPaginatedResponse<AdminUser>>('/admins', { params });
    return response.data;
  }

  async getPendingAdminUsers(): Promise<ApiResponse<AdminUser[]>> {
    // No specific "pending" endpoint exists, use /admins and filter for pending/new users
    this.logger.debug('No specific pending endpoint available, using /admins');
    try {
      const response = await this.api.get<ApiPaginatedResponse<AdminUser>>('/admins');

      const paginated = response.data;
      const adminsList = paginated?.data?.items ?? [];

      // Filter for users that could be considered "pending" (recently created, low access, etc.)
      // Since there's no actual "pending" status, return all admins for management
      return {
        data: adminsList,
        message: paginated?.message || 'Admin users retrieved successfully',
        status: paginated?.status ?? 'success',
        timestamp: paginated?.timestamp ?? new Date().toISOString(),
      };
    } catch (error) {
      this.logger.error('Failed to get admin users:', error);
      // Return empty array if endpoint fails
      return {
        data: [],
        message: 'No admin users available',
        status: 'error',
        timestamp: new Date().toISOString(),
      };
    }
  }

  async promoteToAdmin(userId: string, role: string): Promise<ApiResponse<AdminUser>> {
    // Use the admins endpoint for creating new admin users
    const response = await this.api.post<ApiResponse<AdminUser>>('/admins', { user_id: userId, role });
    return response.data;
  }

  async promoteToAdminByEmail(email: string, role: string, notes?: string, status?: string): Promise<ApiResponse<AdminUser>> {
    // Use the new email-based endpoint for promoting users
    const payload = {
      role,
      ...(notes && { notes }),
      ...(status && { status }) // Include status if provided (for restoring access)
    };
    const response = await this.api.put<ApiResponse<AdminUser>>(`/admins/by-email/${email}`, payload);
    return response.data;
  }

  async revokeAdminAccess(userId: string): Promise<ApiResponse<void>> {
    // Use DELETE on the admins endpoint to revoke access
    const response = await this.api.delete<ApiResponse<void>>(`/admins/${userId}`);
    return response.data;
  }

  async updateAdminUserRole(userId: string, role: string): Promise<ApiResponse<AdminUser>> {
    // Use PUT on the admins endpoint to update role
    const response = await this.api.put<ApiResponse<AdminUser>>(`/admins/${userId}`, { role });
    return response.data;
  }

  async verifyAdminAccess(): Promise<VerifyAdminAccessResponse> {
    const response = await this.api.get<ApiResponse<VerifyAdminAccessPayload>>('/verify');

    const apiResponse = response.data;
    const payload = apiResponse?.data;

    return {
      is_admin: payload?.is_admin ?? false,
      admin_info: payload?.admin_info,
      data: apiResponse?.data,
      status: apiResponse?.status,
      message: apiResponse?.message,
      timestamp: apiResponse?.timestamp,
    };
  }

  async getAdminProfile(): Promise<ApiResponse<{ email: string; role: string; permissions: string[] }>> {
    const response = await this.api.get<ApiResponse<{ email: string; role: string; permissions: string[] }>>('/profile');
    return response.data;
  }

  /**
   * Record admin login activity by calling verify endpoint
   * The /verify endpoint automatically updates last_login_at as a side effect
   */
  async recordLoginActivity(): Promise<ApiResponse<{ timestamp: string; last_login_at?: string }>> {
    try {
      const response = await this.verifyAdminAccess();
      const lastLoginAt = response.admin_info?.last_login_at;

      return {
        data: {
          timestamp: lastLoginAt || new Date().toISOString(),
          last_login_at: lastLoginAt
        },
        status: lastLoginAt ? 'success' : 'error',
        message: lastLoginAt
          ? 'Login activity recorded successfully'
          : 'No login timestamp available',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      this.logger.warn('Failed to record login activity:', error);
      return {
        data: { timestamp: new Date().toISOString() },
        status: 'error',
        message: 'Login tracking failed but authentication succeeded',
        timestamp: new Date().toISOString()
      };
    }
  }

  async getOpsUsers(
    filters?: { status?: string; role?: string },
    pagination?: PaginationParams
  ): Promise<ApiPaginatedResponse<AdminUser>> {
    // Use the correct /admins endpoint instead of non-existent /ops-users
    const params = { ...filters, ...pagination };
    const response = await this.api.get<ApiPaginatedResponse<AdminUser>>('/admins', { params });
    return response.data;
  }

  // Legacy Admin API (for compatibility)
  async getAdmins(
    filters?: { status?: string; role?: string },
    pagination?: PaginationParams
  ): Promise<ApiPaginatedResponse<AdminUser>> {
    const params = { ...filters, ...pagination };
    const response = await this.api.get<ApiPaginatedResponse<AdminUser>>('/admins', { params });
    return response.data;
  }

  async createAdmin(adminData: Omit<AdminUser, 'user_id'>): Promise<ApiResponse<AdminUser>> {
    const response = await this.api.post<ApiResponse<AdminUser>>('/admins', adminData);
    return response.data;
  }

  // Audit Logs API
  async getAuditLogs(
    filters?: {
      admin_user_id?: string;
      action?: string;
      resource_type?: string;
      severity?: string;
      start_date?: string;
      end_date?: string;
    },
    pagination?: PaginationParams
  ): Promise<ApiPaginatedResponse<AuditLog>> {
    const cleanFilters = filters ? Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== undefined && value !== null)
    ) : {};

    const params = { ...cleanFilters, ...pagination };
    const response = await this.api.get<ApiPaginatedResponse<AuditLog>>('/audit-logs', { params });
    return response.data;
  }

  // Custom Role Configuration API
  /**
   * Get custom role configuration for a specific role
   */
  async getCustomRoleConfig(roleId: AdminRole): Promise<RoleConfigResponse> {
    this.logger.debug(`Getting custom role configuration for ${roleId}`);
    const response = await this.api.get<RoleConfigResponse>(`/roles/${roleId}/config`);
    return response.data;
  }

  /**
   * Update custom role configuration for a specific role  
   * Uses PUT endpoint if config exists, POST if it doesn't
   */
  async updateCustomRoleConfig(roleId: AdminRole, request: RoleConfigUpdateRequest): Promise<RoleConfigResponse> {
    this.logger.debug(`Updating custom role configuration for ${roleId}`, request);

    // Validate role ID - prevent modification of super_admin
    if (roleId === 'super_admin') {
      throw new Error('Super admin role configuration cannot be modified');
    }

    try {
      // Try PUT first (update existing)
      const response = await this.api.put<RoleConfigResponse>(`/roles/${roleId}/config`, request);
      return response.data;
    } catch (error) {
      // If 404, try POST (create new)
      if ((error as RequestErrorWithStatus)?.response?.status === 404) {
        const response = await this.api.post<RoleConfigResponse>(`/roles/${roleId}/config`, request);
        return response.data;
      }
      throw error;
    }
  }

  /**
   * Create custom role configuration (if not exists)
   */
  async createCustomRoleConfig(roleId: AdminRole, config: CustomRoleConfig): Promise<RoleConfigResponse> {
    this.logger.debug(`Creating custom role configuration for ${roleId}`, config);

    // Validate role ID - prevent creation of super_admin config
    if (roleId === 'super_admin') {
      throw new Error('Super admin role configuration cannot be customized');
    }

    const response = await this.api.post<RoleConfigResponse>(`/roles/${roleId}/config`, config);
    return response.data;
  }

  /**
   * Get all custom role configurations
   */
  async getAllCustomRoleConfigs(): Promise<{
    data: Record<AdminRole, CustomRoleConfig>;
    message: string;
    status: string;
    timestamp: string;
  }> {
    this.logger.debug('Getting all custom role configurations');
    const response = await this.api.get<{
      data: Record<AdminRole, CustomRoleConfig>;
      message: string;
      status: string;
      timestamp: string;
    }>('/roles/configs');
    return response.data;
  }

  /**
   * Reset custom role configuration to default
   */
  async resetRoleConfigToDefault(roleId: AdminRole): Promise<RoleConfigResponse> {
    this.logger.debug(`Resetting role configuration to default for ${roleId}`);

    // Validate role ID - prevent reset of super_admin
    if (roleId === 'super_admin') {
      throw new Error('Super admin role configuration cannot be reset');
    }

    const response = await this.api.delete<RoleConfigResponse>(`/roles/${roleId}/config`);
    return response.data;
  }
}

